/**
 * Minimal two-pane attestation diff. Compares two JSON-serialisable
 * attestation snapshots and highlights added/removed/changed keys with
 * semantic color tokens. Not a full text differ — enough to compare
 * manifest versions at a glance.
 */
import * as React from "react";
import { cn } from "@/lib/utils";

type Json = unknown;

interface Row {
  key: string;
  left: string;
  right: string;
  state: "same" | "changed" | "added" | "removed";
}

function flatten(obj: Json, prefix = ""): Record<string, string> {
  const out: Record<string, string> = {};
  if (obj === null || obj === undefined) return out;
  if (typeof obj !== "object") {
    out[prefix || "value"] = String(obj);
    return out;
  }
  for (const [k, v] of Object.entries(obj as Record<string, Json>)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) Object.assign(out, flatten(v, key));
    else out[key] = Array.isArray(v) ? `[${v.length}] ${v.join(", ")}` : String(v);
  }
  return out;
}

export function AttestationDiff({ a, b }: { a: Json; b: Json }) {
  const rows: Row[] = React.useMemo(() => {
    const fa = flatten(a);
    const fb = flatten(b);
    const keys = Array.from(new Set([...Object.keys(fa), ...Object.keys(fb)])).sort();
    return keys.map((k) => {
      const left = fa[k] ?? "";
      const right = fb[k] ?? "";
      let state: Row["state"] = "same";
      if (!(k in fa)) state = "added";
      else if (!(k in fb)) state = "removed";
      else if (left !== right) state = "changed";
      return { key: k, left, right, state };
    });
  }, [a, b]);

  return (
    <div className="rounded-md border border-border bg-card text-xs">
      <div className="grid grid-cols-[1fr_1fr_1fr] border-b border-border bg-muted/40 px-3 py-2 font-medium text-muted-foreground uppercase tracking-wider">
        <div>Field</div>
        <div>Version A</div>
        <div>Version B</div>
      </div>
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li
            key={r.key}
            className={cn(
              "grid grid-cols-[1fr_1fr_1fr] gap-2 px-3 py-2 font-mono",
              r.state === "added" && "bg-success-muted/40",
              r.state === "removed" && "bg-destructive/5",
              r.state === "changed" && "bg-warning-muted/40",
            )}
          >
            <span className="truncate text-muted-foreground">{r.key}</span>
            <span className="truncate">{r.left || "—"}</span>
            <span className="truncate">{r.right || "—"}</span>
          </li>
        ))}
        {rows.length === 0 && (
          <li className="px-3 py-6 text-center text-muted-foreground">No fields to diff.</li>
        )}
      </ul>
    </div>
  );
}
